import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api, apiError } from "../api/client";
import { useAuth } from "../store/auth";

const VALID_PLANS = ["solo", "pro", "label"] as const;
type SignupPlan = (typeof VALID_PLANS)[number];

export default function Signup() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [searchParams] = useSearchParams();

  // The landing CTAs link to /signup?plan=solo|pro|label. We default to "solo"
  // if the param is missing or invalid (e.g. someone hand-typed /signup).
  const planParam = searchParams.get("plan");
  const plan: SignupPlan = (VALID_PLANS as readonly string[]).includes(planParam || "")
    ? (planParam as SignupPlan)
    : "solo";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast.error(t("auth.signup.passwordConfirmError"));
      return;
    }
    setLoading(true);
    try {
      // Step 1: create the account.
      const { data } = await api.post("/api/auth/signup", { email, password, name: name.trim() || undefined });
      setSession(data.token, data.user);

      // Step 2: open Stripe Checkout for the selected plan with a 14-day trial.
      try {
        const checkout = await api.post("/api/billing/checkout", { plan });
        if (checkout.data?.url) {
          window.location.href = checkout.data.url;
          return;
        }
      } catch {
        // Checkout failed — user is already logged in; send to Settings
        // so they can pick a plan there.
      }
      navigate("/settings", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-600 shadow-lg shadow-brand-900/40"
            aria-hidden="true"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
              <rect x="3.5" y="14" width="3" height="6.5" rx="1" />
              <rect x="9" y="10" width="3" height="10.5" rx="1" />
              <rect x="14.5" y="6" width="3" height="14.5" rx="1" />
              <circle cx="20" cy="4.7" r="2.2" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold">{t("auth.signup.title")}</h1>
          <p className="mt-1 text-sm text-slate-400">{t("auth.signup.subtitle")}</p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-200 ring-1 ring-brand-400/30">
            {t("auth.signup.planBadge", {
              plan: t(`landing.pricing.plans.${plan}.name`),
            })}
          </p>
        </div>
        <form onSubmit={onSubmit} className="card space-y-4">
          <div>
            <label className="label">{t("auth.signup.email")}</label>
            <input
              type="email"
              required
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label">{t("auth.signup.name")}</label>
            <input
              type="text"
              className="input"
              placeholder={t("auth.signup.nameHint")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div>
            <label className="label">{t("auth.signup.password")}</label>
            <input
              type="password"
              required
              minLength={8}
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <p className="mt-1 text-xs text-slate-500">{t("auth.signup.passwordHint")}</p>
          </div>
          <div>
            <label className="label">{t("auth.signup.passwordConfirm")}</label>
            <input
              type="password"
              required
              minLength={8}
              className="input"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? t("auth.signup.submitting") : t("auth.signup.submitWithTrial")}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            {t("auth.signup.trialDisclaimer")}
          </p>
        </form>
        <p className="mt-4 text-center text-sm text-slate-400">
          {t("auth.signup.alreadyHave")}{" "}
          <Link to="/login" className="text-brand-300 hover:text-brand-200">
            {t("auth.signup.signIn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
